/**
 * Datoformatering. ISO 8601 i API og database, dansk format i UI.
 *
 * Kort format bruger bindestreg ("20-04-2026"), som PRD kraever. Intl med da-DK giver
 * punktummer ("20.04.2026"), saa den kan ikke bruges her - formateringen laves manuelt.
 */

const MONTHS_DA = [
  'januar',
  'februar',
  'marts',
  'april',
  'maj',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'december',
] as const;

/** Soendag foerst, som `Date.getDay()` taeller. */
const WEEKDAYS_DA = [
  'søndag',
  'mandag',
  'tirsdag',
  'onsdag',
  'torsdag',
  'fredag',
  'lørdag',
] as const;

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "20-04-2026" - tabeller og UI. */
export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${date.getFullYear()}`;
}

/** "20-04-26" - storskaermenes raekker, hvor aarhundredet ikke er plads til (Storskaerm-2-Spec.md). */
export function formatDateShortYear(value: string | Date | null | undefined): string {
  const formatted = formatDate(value);
  return formatted === '—' ? formatted : `${formatted.slice(0, 6)}${formatted.slice(8)}`;
}

/** "20. april 2026" - overskrifter, rapporter, emails. */
export function formatDateLong(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return `${date.getDate()}. ${MONTHS_DA[date.getMonth()]} ${date.getFullYear()}`;
}

/** "april 2026" - fakturalinjer for recurring, maanedsrapporter. */
export function formatMonthYear(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return `${MONTHS_DA[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * "apr" - maaneden alene, forkortet. Til akser med tolv maaneder, hvor "april 2026" ikke er
 * plads til under hver soejle.
 */
export function formatMonthShort(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return (MONTHS_DA[date.getMonth()] ?? '').slice(0, 3);
}

/** "april" - maaneden alene, uforkortet. */
export function formatMonthName(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return MONTHS_DA[date.getMonth()] ?? '—';
}

/** "mandag" - ugedagen. */
export function formatWeekday(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return WEEKDAYS_DA[date.getDay()] ?? '—';
}

/** "14:32" - klokkeslaettet alene. */
export function formatTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mi}`;
}

/** "20-04-2026 14:32" - audit-log og synk-historik. */
export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return `${formatDate(date)} ${formatTime(date)}`;
}

/** ISO-datodel, "2026-04-20". Formatet der gaar til API og database. */
export function toIsoDate(value: string | Date): string {
  const date = toDate(value);
  if (!date) throw new Error(`Ugyldig dato: ${String(value)}`);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mm}-${dd}`;
}

/**
 * "for 2 timer siden". Bruges paa alarmkort.
 * `now` er en parameter, saa funktionen er deterministisk i test.
 */
export function formatRelative(
  value: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  const date = toDate(value);
  if (!date) return '—';
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  const future = seconds < 0;
  const abs = Math.abs(seconds);

  const label = (n: number, singular: string, plural: string): string =>
    `${n} ${n === 1 ? singular : plural}`;

  let phrase: string;
  if (abs < 60) phrase = 'et øjeblik';
  else if (abs < 3600) phrase = label(Math.floor(abs / 60), 'minut', 'minutter');
  else if (abs < 86400) phrase = label(Math.floor(abs / 3600), 'time', 'timer');
  else if (abs < 2592000) phrase = label(Math.floor(abs / 86400), 'dag', 'dage');
  else if (abs < 31536000) phrase = label(Math.floor(abs / 2592000), 'måned', 'måneder');
  else phrase = label(Math.floor(abs / 31536000), 'år', 'år');

  return future ? `om ${phrase}` : `for ${phrase} siden`;
}

/** Hele dage mellem to datoer. Positiv naar `to` ligger efter `from`. */
export function daysBetween(from: string | Date, to: string | Date): number {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b) throw new Error('Ugyldig dato i daysBetween');
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((utcB - utcA) / 86_400_000);
}

/** Laegger dage til en dato og returnerer ISO-datodelen. */
export function addDays(value: string | Date, days: number): string {
  const date = toDate(value);
  if (!date) throw new Error(`Ugyldig dato: ${String(value)}`);
  const result = new Date(date.getTime());
  result.setDate(result.getDate() + days);
  return toIsoDate(result);
}

/** Laegger maaneder til en dato. Klipper til maanedens sidste dag (31. jan + 1 md = 28./29. feb). */
export function addMonths(value: string | Date, months: number): string {
  const date = toDate(value);
  if (!date) throw new Error(`Ugyldig dato: ${String(value)}`);
  const day = date.getDate();
  const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDay));
  return toIsoDate(result);
}

/** Foerste og sidste dag i maaneden som ISO-datoer. */
export function monthBounds(value: string | Date): { start: string; end: string } {
  const date = toDate(value);
  if (!date) throw new Error(`Ugyldig dato: ${String(value)}`);
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

/** Kvartalet datoen ligger i: nummeret 1-4 og foerste og sidste dag som ISO-datoer. */
export function quarterBounds(value: string | Date): {
  quarter: number;
  start: string;
  end: string;
} {
  const date = toDate(value);
  if (!date) throw new Error(`Ugyldig dato: ${String(value)}`);
  const first = Math.floor(date.getMonth() / 3) * 3;
  return {
    quarter: first / 3 + 1,
    start: toIsoDate(new Date(date.getFullYear(), first, 1)),
    end: toIsoDate(new Date(date.getFullYear(), first + 3, 0)),
  };
}

/** Sidste afsluttede maaned - default-perioden i sprint-faktureringsflowet. */
export function previousMonthBounds(now: string | Date): { start: string; end: string } {
  const date = toDate(now);
  if (!date) throw new Error(`Ugyldig dato: ${String(now)}`);
  return monthBounds(new Date(date.getFullYear(), date.getMonth() - 1, 15));
}

/**
 * Den seneste uge til og med i dag - default-perioden i sprint-faktureringsflowet.
 *
 * Otte dage inklusive begge ender, ikke syv: `start` er dagen for en uge siden, og `end` er i
 * dag. En faktura skrives typisk i slutningen af den uge, arbejdet blev udfoert i, og en
 * periode der stoppede i gaar ville da udelade den dag, controlleren sidder og fakturerer for.
 *
 * Afloeser `previousMonthBounds` som standard paa /fakturering/ny. Den funktion bliver, fordi
 * maanedsrapporten stadig bruger den.
 */
export function lastWeekBounds(now: string | Date): { start: string; end: string } {
  const today = toDate(now);
  if (!today) throw new Error(`Ugyldig dato: ${String(now)}`);
  const end = toIsoDate(today);
  return { start: addDays(end, -7), end };
}

/** True hvis to lukkede datointervaller overlapper. Bruges mod dobbeltfakturering. */
export function periodsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}
